import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { retryPayment, resendInvoice, refundPayment } from "../api/billingActions";

/**
 * Mutations for admin billing support actions (retry / resend / refund).
 * All invalidate the relevant queries so the UI refreshes after an action.
 */
export const useBillingActions = (companyId) => {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["accountHealth", companyId] });
    queryClient.invalidateQueries({ queryKey: ["subscriptionDetails", companyId] });
    queryClient.invalidateQueries({ queryKey: ["paymentHistory", companyId] });
    queryClient.invalidateQueries({ queryKey: ["paymentHistoryDetail", companyId] });
    queryClient.invalidateQueries({ queryKey: ["webhookHistory", companyId] });
  };

  const retry = useMutation({
    mutationFn: () => retryPayment(companyId),
    onSuccess: (res) => {
      const paid = res?.result?.paid;
      toast[paid ? "success" : "info"](
        paid ? "Payment retried and succeeded." : "Retry submitted — awaiting confirmation."
      );
      invalidate();
    },
    onError: (error) => toast.error(error.message || "Failed to retry payment."),
  });

  const resend = useMutation({
    mutationFn: (invoiceId) => resendInvoice(companyId, invoiceId),
    onSuccess: () => {
      toast.success("Invoice email resent to the customer.");
      invalidate();
    },
    onError: (error) => toast.error(error.message || "Failed to resend invoice."),
  });

  const refund = useMutation({
    mutationFn: (payload) => refundPayment(companyId, payload),
    onSuccess: (res) => {
      const amount = res?.result?.amount;
      toast.success(`Refund issued${amount != null ? ` ($${Number(amount).toFixed(2)})` : ""}.`);
      invalidate();
    },
    onError: (error) => toast.error(error.message || "Failed to issue refund."),
  });

  return { retry, resend, refund };
};
